'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

interface RightEdgePanelProps {
  /** Content rendered inside the slide-out panel */
  children: React.ReactNode
  /** Icon shown on the toggle button when the panel is closed */
  toggleIcon: React.ReactNode
  /** Accessible label for the toggle button */
  ariaLabel?: string
  /** Additional classes for the panel container */
  panelClassName?: string
  /** Additional classes for the outer wrapper */
  className?: string
  /** Callback fired when the panel opens or closes */
  onToggle?: (isOpen: boolean) => void
}

export function RightEdgePanel({
  children,
  toggleIcon,
  ariaLabel = 'Toggle panel',
  panelClassName,
  className,
  onToggle,
}: RightEdgePanelProps) {
  const [isOpen, setIsOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    const next = !isOpen
    setIsOpen(next)
    onToggle?.(next)
  }

  const handleClose = useCallback(() => {
    setIsOpen(false)
    onToggle?.(false)
  }, [onToggle])

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        handleClose()
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleClose])

  return (
    <div className={className}>
      {/* top-4 as well as bottom-20 gives the column a definite height, so the panel can cap itself
          against the viewport instead of growing off the top. */}
      <div
        ref={panelRef}
        className="fixed top-4 right-0 bottom-20 z-40 flex flex-col items-end justify-end gap-3 pointer-events-none"
      >
        {/* Slide-out panel */}
        <div
          className={`relative min-h-0 overflow-y-auto overscroll-contain scrollbar-dark bg-black rounded-l-[8px] shadow-lg transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full'
          } ${panelClassName ?? ''}`}
        >
          {children}
        </div>

        {/* Toggle / Close button */}
        <button
          onClick={handleToggle}
          className="pointer-events-auto flex shrink-0 items-center gap-2.5 bg-black text-white pl-4 pr-2.5 py-2 rounded-l-full shadow-lg transition-colors hover:bg-black/80"
          aria-label={ariaLabel}
          aria-expanded={isOpen}
        >
          {isOpen ? <X className="size-6" /> : toggleIcon}
        </button>
      </div>
    </div>
  )
}
